import { useMemo, useState } from 'react';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import BYDBanner from './images/kv-pc.jpg';
import BYDBannerMobile from './images/01-kv-banner-mob.webp';
import SeagullExterior1 from './images/07-01-pc-01.webp';
import SeagullExterior2 from './images/2023-byd-seagull.jpg';
import SeagullInterior5 from './images/10-pc.jpg';
import SeagullInterior11 from './images/10-01-pc-02.webp';
import SeagullCharging15 from './images/10-01-pc-04.jpg';
import SeagullPerformance16 from './images/08-01-pc-03.webp';
import Seagull25 from './images/07-02-pc-01.webp';
import SeagullBlack from './images/04-black.webp';
import SeagullBlue from './images/02-blue.webp';
import SeagullGreen from './images/byd-green.png';
import SeagullWhite from './images/03-white.webp';
import PageSeo, {
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from './PageSeo';

function JelajahiSeagull() {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [selectedColor, setSelectedColor] = useState('black');

  const colorOptions = useMemo(
    () => [
      {
        label: 'Cosmos Black',
        value: 'black',
        swatch: '#101010',
        image: SeagullBlack,
      },
      {
        label: 'Apricity White',
        value: 'white',
        swatch: '#f3f2ee',
        image: SeagullWhite,
      },
      {
        label: 'Sprout Green',
        value: 'green',
        swatch: '#9ec15d',
        image: SeagullGreen,
      },
      {
        label: 'Misty Blue',
        value: 'blue',
        swatch: '#7fb8ce',
        image: SeagullBlue,
      },
    ],
    [],
  );

  const selectedOption =
    colorOptions.find((option) => option.value === selectedColor) ||
    colorOptions[0];

  return (
    <>
      <PageSeo
        title='BYD Atto 1 BSD Serpong | Harga City Car Listrik 2026'
        description='Jelajahi BYD Atto 1 di BYD BSD Serpong. Cek harga Dynamic dan Premium, spesifikasi, warna, promo, dan test drive city car listrik BYD.'
        keywords={[
          ...keywordSets.localDealer,
          'harga BYD Atto 1 BSD',
          'BYD Atto 1 Tangerang',
          'city car listrik BYD',
          'BYD Atto1 Indonesia',
        ]}
        path='/jelajahi-atto1'
        image={BYDBanner}
        jsonLd={[
          productJsonLd({
            name: 'BYD Atto 1',
            description:
              'City car listrik BYD Atto 1 dengan Blade Battery, layar 10.1 inci, CarPlay, Android Auto, dan varian Dynamic serta Premium.',
            image: BYDBanner,
            path: '/jelajahi-atto1',
            lowPrice: 205000000,
            highPrice: 241000000,
            offerCount: 2,
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'BYD Atto 1', path: '/jelajahi-atto1' },
          ]),
        ]}
      />

      <Box component='main' sx={{ width: '100%', overflow: 'hidden' }}>
        <Box
          component='h1'
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          Jelajahi BYD Atto 1
        </Box>

        <HeroSection isMobile={isMobile} />
        <OverviewSection />
        <ColorSection
          colorOptions={colorOptions}
          selectedOption={selectedOption}
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
        />
        <FeatureBand
          image={SeagullExterior1}
          title='Kompak untuk harian kota'
          description='Dimensi 3925 x 1720 x 1550 mm membuat Atto 1 mudah masuk area padat, parkir di ruang terbatas, dan tetap nyaman untuk aktivitas harian.'
        />
        <FeatureBand
          image={SeagullInterior5}
          title='Kabin modern dan intuitif'
          description='Layar sentuh 10.1 inci, panel instrumen digital, konektivitas smartphone, dan layout kabin yang simpel membuat perjalanan terasa praktis.'
          reverse
        />
        <FeatureGrid />
        <FeatureBand
          image={SeagullExterior2}
          title='Desain muda dan futuristik'
          description='Garis bodi tegas, velg 16 inci, dan DRL LED memberi karakter city car listrik yang segar tanpa terasa berlebihan.'
        />
        <CtaSection />
      </Box>
    </>
  );
}

function HeroSection({ isMobile }) {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '92vh', md: '100vh' },
        pt: { xs: 9, md: 10 },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        backgroundColor: '#101418',
      }}
    >
      <Box
        component='img'
        src={isMobile ? BYDBannerMobile : BYDBanner}
        alt='BYD Atto 1 city car listrik'
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.04) 46%, rgba(0,0,0,0.62) 100%)',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          pb: { xs: 5, md: 7 },
          color: '#fff',
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '2.4rem', sm: '3.5rem', md: '5rem' },
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          BYD Atto 1
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 650,
            fontSize: { xs: '1rem', md: '1.18rem' },
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.9)',
          }}
        >
          City car listrik kompak untuk mobilitas harian, dengan Blade Battery,
          fitur modern, dan pilihan varian Dynamic serta Premium.
        </Typography>

        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
            gap: 1,
            maxWidth: 850,
          }}
        >
          {[
            { value: '300-380 km', label: 'NEDC range' },
            { value: '55 kW', label: 'Tenaga maksimum' },
            { value: '4.9 detik', label: '0-50 km/jam' },
            { value: 'Rp205 jt', label: 'Harga mulai' },
          ].map((spec) => (
            <Box
              key={spec.label}
              sx={{
                border: '1px solid rgba(255,255,255,0.24)',
                backgroundColor: 'rgba(0,0,0,0.36)',
                px: 1.8,
                py: 1.4,
                borderRadius: '8px',
              }}
            >
              <Typography sx={{ fontSize: '1.18rem', fontWeight: 900 }}>
                {spec.value}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', opacity: 0.82 }}>
                {spec.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function OverviewSection() {
  return (
    <Box
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#f4f7fb',
      }}
    >
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            sx={{
              color: '#4c5867',
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontSize: '0.82rem',
            }}
          >
            City Car Listrik
          </Typography>
          <Typography
            component='h2'
            sx={{
              mt: 1,
              fontSize: { xs: '2rem', md: '3.2rem' },
              fontWeight: 900,
              color: '#111827',
              lineHeight: 1.05,
            }}
          >
            Simpel, efisien, dan siap dipakai setiap hari
          </Typography>
        </Box>
        <Typography
          sx={{
            color: '#4b5563',
            fontSize: { xs: '1rem', md: '1.08rem' },
            lineHeight: 1.8,
          }}
        >
          Atto 1 dibuat untuk mobilitas urban: mudah dikendarai, hemat energi,
          kabin tetap praktis, dan fitur digitalnya cukup lengkap untuk
          kebutuhan harian. Ukurannya kompak, tetapi tetap memberi rasa modern
          khas mobil listrik BYD.
        </Typography>
      </Box>
    </Box>
  );
}

function ColorSection({
  colorOptions,
  selectedOption,
  selectedColor,
  setSelectedColor,
}) {
  return (
    <Box
      sx={{
        minHeight: { xs: 'auto', md: '100vh' },
        backgroundColor: '#fff',
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 360px' },
          gap: { xs: 4, lg: 5 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            sx={{
              color: '#4c5867',
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontSize: '0.82rem',
            }}
          >
            Exterior Color
          </Typography>
          <Typography
            component='h2'
            sx={{
              mt: 1,
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              color: '#111827',
              lineHeight: 1.05,
            }}
          >
            Pilih warna Atto 1
          </Typography>
          <Box
            sx={{
              mt: 2,
              height: { xs: 270, sm: 380, md: 500 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component='img'
              src={selectedOption.image}
              alt={`BYD Atto 1 warna ${selectedOption.label}`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: 'drop-shadow(0 24px 24px rgba(16,24,40,0.2))',
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            borderRadius: '8px',
            border: '1px solid rgba(17,24,39,0.12)',
            backgroundColor: '#f8fafc',
            p: { xs: 2, sm: 3 },
            boxShadow: '0 12px 26px rgba(16,24,40,0.08)',
          }}
        >
          <Typography
            sx={{
              color: '#667085',
              fontWeight: 800,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              fontSize: '0.78rem',
            }}
          >
            Selected color
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: '2rem', fontWeight: 900 }}>
            {selectedOption.label}
          </Typography>
          <Typography sx={{ mt: 1, color: '#4b5563', lineHeight: 1.6 }}>
            Pilih warna eksterior yang paling cocok dengan gaya harian kamu.
          </Typography>

          <Box sx={{ mt: 3, display: 'grid', gap: 1 }}>
            {colorOptions.map((option) => {
              const active = selectedColor === option.value;

              return (
                <Button
                  key={option.value}
                  type='button'
                  onClick={() => setSelectedColor(option.value)}
                  variant='outlined'
                  sx={{
                    justifyContent: 'flex-start',
                    gap: 1.5,
                    py: 1.2,
                    px: 1.3,
                    borderRadius: '8px',
                    textTransform: 'none',
                    color: '#111827',
                    borderColor: active
                      ? '#2d6cdf'
                      : 'rgba(17,24,39,0.16)',
                    backgroundColor: active ? '#edf4ff' : '#fff',
                    '&:hover': {
                      borderColor: '#2d6cdf',
                      backgroundColor: active ? '#e4efff' : '#f7f9fc',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      flex: '0 0 auto',
                      backgroundColor: option.swatch,
                      border:
                        option.value === 'white'
                          ? '1px solid rgba(17,24,39,0.24)'
                          : '1px solid rgba(255,255,255,0.42)',
                      boxShadow: active
                        ? '0 0 0 3px rgba(45,108,223,0.22)'
                        : '',
                    }}
                  />
                  <Typography sx={{ fontWeight: 800 }}>{option.label}</Typography>
                </Button>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function FeatureBand({ image, title, description, reverse = false }) {
  return (
    <Box
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: reverse ? '#111827' : '#f4f7fb',
      }}
    >
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
          gap: { xs: 3, md: 5 },
          alignItems: 'center',
        }}
      >
        <Box sx={{ order: { xs: 1, md: reverse ? 2 : 1 } }}>
          <Box
            component='img'
            src={image}
            alt={title}
            sx={{
              width: '100%',
              height: { xs: 280, sm: 390, md: 500 },
              objectFit: 'cover',
              borderRadius: '8px',
              display: 'block',
              boxShadow: reverse
                ? '0 24px 42px rgba(0,0,0,0.34)'
                : '0 18px 32px rgba(16,24,40,0.12)',
            }}
          />
        </Box>
        <Box
          sx={{
            order: { xs: 2, md: reverse ? 1 : 2 },
            color: reverse ? '#fff' : '#111827',
          }}
        >
          <Typography
            component='h2'
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              lineHeight: 1.08,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 2,
              color: reverse ? 'rgba(255,255,255,0.78)' : '#4b5563',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.08rem' },
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function FeatureGrid() {
  const items = [
    {
      image: SeagullInterior11,
      title: 'Kontrol praktis',
      desc: 'Setir multifungsi, head unit intuitif, dan pengaturan kursi elektrik pada varian Premium.',
    },
    {
      image: SeagullCharging15,
      title: 'Bagasi fleksibel',
      desc: 'Kursi belakang dapat dilipat untuk membawa barang lebih banyak saat dibutuhkan.',
    },
    {
      image: SeagullPerformance16,
      title: 'Keselamatan lengkap',
      desc: 'ABS, EBD, ESC, TPMS, airbag, ISOFIX, dan pengereman regeneratif untuk pemakaian harian.',
    },
    {
      image: Seagull25,
      title: 'Lincah di kota',
      desc: 'Radius putar 4.95 meter membuat manuver di jalan sempit dan area parkir lebih mudah.',
    },
  ];

  return (
    <Box sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
            lineHeight: 1.08,
            mb: 3,
          }}
        >
          Fitur yang terasa setiap hari
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2.5,
          }}
        >
          {items.map((item) => (
            <Box
              key={item.title}
              sx={{
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                boxShadow: '0 12px 26px rgba(16,24,40,0.08)',
                border: '1px solid rgba(17,24,39,0.08)',
              }}
            >
              <Box
                component='img'
                src={item.image}
                alt={item.title}
                sx={{
                  width: '100%',
                  height: { xs: 210, sm: 240 },
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <Box sx={{ p: 2.5 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '1.25rem' }}>
                  {item.title}
                </Typography>
                <Typography sx={{ mt: 1, color: '#4b5563', lineHeight: 1.65 }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CtaSection() {
  return (
    <Box
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#f4f7fb',
      }}
    >
      <Box sx={{ maxWidth: 980, mx: 'auto', textAlign: 'center' }}>
        <Typography
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
          }}
        >
          Tertarik dengan BYD Atto 1?
        </Typography>
        <Typography
          sx={{
            mt: 2,
            mx: 'auto',
            maxWidth: 780,
            color: '#4b5563',
            lineHeight: 1.7,
          }}
        >
          Hubungi Junius di BYD BSD Serpong untuk info harga Atto 1, promo,
          simulasi pembelian, dan jadwal test drive.
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Button
            href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20tertarik%20dengan%20BYD%20Atto%201%20dan%20ingin%20tahu%20harga%20serta%20promonya'
            target='_blank'
            variant='contained'
            sx={{
              px: 4,
              py: 1.25,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 800,
              backgroundColor: '#1f5fd1',
              '&:hover': { backgroundColor: '#184ca8' },
            }}
          >
            Tanya Harga Atto 1
          </Button>
          <Button
            href='/pricing'
            variant='outlined'
            sx={{
              px: 4,
              py: 1.25,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Lihat Daftar Harga
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default JelajahiSeagull;
